import {
  loadTowerProgress,
  saveTowerProgress,
  type TowerProgress,
} from "@/tower/tower-storage";

/** Break chain if no battle kill within this window (ms). */
export const KILL_STREAK_IDLE_RESET_MS = 10_000;

export type KillStreakState = {
  current: number;
  best: number;
};

export const CombatKillStreakEvents = {
  BestChanged: "combat-kill-streak-best-changed",
} as const;

const LEGACY_STORAGE_PREFIX = "mind-survival/kill-streak/v1";

function legacyStorageKey(userId: string): string {
  return `${LEGACY_STORAGE_PREFIX}:${userId}`;
}

function readLegacyBestKillStreak(userId: string): number {
  const raw = localStorage.getItem(legacyStorageKey(userId));
  if (!raw) return 0;
  try {
    const parsed = JSON.parse(raw) as { bestKillStreak?: unknown };
    const n = parsed.bestKillStreak;
    if (typeof n !== "number" || !Number.isFinite(n)) return 0;
    return Math.max(0, Math.floor(n));
  } catch {
    localStorage.removeItem(legacyStorageKey(userId));
    return 0;
  }
}

/** One-time merge from old local-only streak save into tower progress (cloud-backed). */
export function migrateLegacyKillStreakBest(userId: string): void {
  const legacy = readLegacyBestKillStreak(userId);
  if (legacy <= 0) return;
  localStorage.removeItem(legacyStorageKey(userId));
  const tower = loadTowerProgress(userId);
  if (legacy <= tower.bestKillStreak) return;
  saveTowerProgress(userId, { ...tower, bestKillStreak: legacy });
}

export function loadCombatBestKillStreak(userId: string): number {
  migrateLegacyKillStreakBest(userId);
  return loadTowerProgress(userId).bestKillStreak;
}

function persistCombatBestKillStreak(userId: string, best: number): void {
  const tower = loadTowerProgress(userId);
  if (best <= tower.bestKillStreak) return;
  const next: TowerProgress = { ...tower, bestKillStreak: best };
  saveTowerProgress(userId, next);
  window.dispatchEvent(
    new CustomEvent(CombatKillStreakEvents.BestChanged, {
      detail: { best },
    }),
  );
}

/**
 * In-memory combo for one battle session only (refresh or leaving battle ends current).
 * Best is stored in tower progress and synced to cloud via gameState.
 */
export class KillStreakTracker {
  private readonly userId: string;
  private readonly onChange: (state: KillStreakState) => void;
  private current = 0;
  private best: number;
  private lastKillAt: number | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(userId: string, onChange: (state: KillStreakState) => void) {
    this.userId = userId;
    this.onChange = onChange;
    this.best = loadCombatBestKillStreak(userId);
    this.emit();
  }

  recordKill(): void {
    const now = Date.now();
    if (
      this.lastKillAt !== null &&
      now - this.lastKillAt > KILL_STREAK_IDLE_RESET_MS
    ) {
      this.current = 0;
    }
    this.current += 1;
    this.lastKillAt = now;
    if (this.current > this.best) {
      this.best = this.current;
      persistCombatBestKillStreak(this.userId, this.best);
    }
    this.scheduleIdleReset();
    this.emit();
  }

  resetCurrent(): void {
    this.clearIdleTimer();
    this.current = 0;
    this.lastKillAt = null;
    this.emit();
  }

  destroy(): void {
    this.clearIdleTimer();
    this.resetCurrent();
  }

  private scheduleIdleReset(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.current = 0;
      this.lastKillAt = null;
      this.idleTimer = null;
      this.emit();
    }, KILL_STREAK_IDLE_RESET_MS);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private emit(): void {
    this.onChange({ current: this.current, best: this.best });
  }
}
